import Data from '@/data'
import { TreeEditingState, TreeProperties } from '@/logic/tree'
import Store from '@/store'
import { TreeAscendancyViewer } from '@/tree/TreeViewer'
import { AscendancyEmbed, TreeHistoryStep } from '@/types/embed'
import Select, { OptionOrGroup } from '@/ui/select'
import Tooltip from '@/ui/tooltip'
import classNames from 'classnames'
import { pick } from 'lodash'
import React from 'react'
import { CopyEmbedAsTooltipBox, CopyEmbedBox } from '../copy'

import WithTreeData from '../WithTreeData'
import './Ascendancy.scss'
import { SelectVersion } from './Passives'

const treeSteps = [
  { tip: 'Normal Labyrinth', name: 'I' },
  { tip: 'Cruel Labyrinth', name: 'II' },
  { tip: 'Merciless Labyrinth', name: 'III' },
  { tip: 'Uber Labyrinth', name: 'IV' }
]

const emptyHistory: number[] = []

type NavState = Pick<TreeEditingState, 'hover' | 'masteryPopup'>

function AscendancyTree({
  state,
  onChange,
  ascendancy,
  version,
  style,
  active
}: {
  state: TreeHistoryStep[][]
  ascendancy: string
  onChange?: (value: TreeHistoryStep[][] | ((prev: TreeHistoryStep[][]) => TreeHistoryStep[][])) => void
  version: number
  style?: React.CSSProperties
  active?: number
}) {
  const [step, setStep] = React.useState(active !== undefined ? active : state.length - 1)
  React.useEffect(() => {
    if (active !== undefined) setStep(active)
  }, [active])

  const history = state[step] ?? state[state.length - 1] ?? emptyHistory

  const readOnly = !onChange
  const treeProperties = React.useMemo<TreeProperties>(
    () => ({
      version,
      charClass: Data.ascendancyClass[ascendancy] ?? 'StrDexInt',
      ascendancy,
      ascendancyOnly: true,
      readOnly,
      limit: {
        normal: 0,
        ascendancy: 8
      }
    }),
    [version, ascendancy, readOnly]
  )

  const [navState, setNavState] = React.useState<NavState>({})

  const treeState = React.useMemo<TreeEditingState>(
    () => ({
      history,
      position: history.length,
      masteries: {},
      jewels: {},
      tattoos: {},
      ...navState
    }),
    [history, navState]
  )
  const onChangeTree = React.useCallback(
    (state: TreeEditingState | ((prev: TreeEditingState) => TreeEditingState)) => {
      if (typeof state === 'function') state = state(treeState)
      if (state === treeState) return

      setNavState(pick(state, ['hover', 'masteryPopup']))
      const history = state.history
      onChange?.((steps) => {
        steps = steps.slice()
        while (steps.length < step) {
          steps.push(steps[steps.length - 1])
        }
        steps[step] = history
        return steps
      })
    },
    [treeState, onChange, step]
  )
  return (
    <div className="poe-AscendancyEmbed" style={style}>
      <WithTreeData version={version}>
        <TreeAscendancyViewer tree={treeProperties} state={treeState} setState={onChangeTree} />
      </WithTreeData>
      {treeSteps.map((info, index) => {
        const angle = Math.PI * (index - 1.5) * 0.1
        const x = 170 + 180 * Math.sin(angle) - 20
        const y = 170 + 180 * Math.cos(angle) - 20
        return (
          <Tooltip key={index} tooltip={info.tip}>
            <div
              className={classNames(`poe-button poe-button-${index + 1}`, {
                'poe-active': index === step
              })}
              onClick={() => setStep(index)}
              style={{ left: x, top: y }}>
              {info.name}
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

export function AscendancyEmbedView({
  embed,
  active,
  style
}: {
  embed: AscendancyEmbed
  active?: number
  style?: React.CSSProperties
}) {
  return (
    <AscendancyTree
      version={embed.version}
      state={embed.steps}
      ascendancy={embed.ascendancy}
      active={active}
      style={style}
    />
  )
}

type AscendancyOption = {
  id: string
  name: string
}

export function SelectAnyAscendancy({
  value,
  onChange,
  version
}: {
  value: string
  onChange: (value: string) => void
  version?: number
}) {
  const ascendancy = Data.characters[Data.ascendancyClass[value]!]?.ascendancies[value]
  const getOptions = React.useCallback(
    (): OptionOrGroup<AscendancyOption>[] =>
      Object.values(Data.characters).map((classData) => ({
        title: classData.name,
        options: Object.entries(classData.ascendancies).map(([id, ascendancy]) => ({
          id,
          name: version === 326 ? ascendancy.league_name : ascendancy.name
        }))
      })),
    [version]
  )
  const onSelect = React.useCallback((opt?: AscendancyOption) => opt && onChange(opt.id), [onChange])
  return (
    <Select
      className="ui-Select"
      options={getOptions}
      value={
        ascendancy
          ? {
              id: value!,
              name: version === 326 ? ascendancy.league_name : ascendancy.name
            }
          : undefined
      }
      onChange={onSelect}
      placeholder="Unknown Ascendancy"
    />
  )
}

const withTreeData = (Component: React.ComponentType<{}>) =>
  function () {
    const embed = Store.useEmbed()
    if (!('version' in embed)) return null
    return (
      <WithTreeData version={embed.version}>
        <Component />
      </WithTreeData>
    )
  }

export default withTreeData(function AscendancyEditor() {
  const embed = Store.useEmbed()
  const dispatch = Store.useDispatch()
  const onChangeAscendancy = React.useCallback(
    (value: string) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'ascendancy') return embed
        embed = { ...embed }
        embed.steps = [[]]
        embed.ascendancy = value
        return embed
      }),
    [dispatch]
  )
  const onChange = React.useCallback(
    (value: TreeHistoryStep[][] | ((prev: TreeHistoryStep[][]) => TreeHistoryStep[][])) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'ascendancy') return embed
        embed = { ...embed }
        if (typeof value === 'function') value = value(embed.steps)
        embed.steps = value
        return embed
      }),
    [dispatch]
  )

  const onChangeVersion = React.useCallback(
    (value: number) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'ascendancy') return embed
        embed = { ...embed }
        embed.version = value
        embed.steps = [[]]
        return embed
      }),
    [dispatch]
  )

  if (embed.type !== 'ascendancy') return null

  return (
    <div className="poe-AscendancyEditor">
      <div>
        <SelectVersion version={embed.version} onChange={onChangeVersion} />
        <SelectAnyAscendancy value={embed.ascendancy} onChange={onChangeAscendancy} version={embed.version} />
      </div>
      <AscendancyTree version={embed.version} state={embed.steps} ascendancy={embed.ascendancy} onChange={onChange} />
      <CopyEmbedBox type="ascendancy" />
      <CopyEmbedAsTooltipBox type="ascendancy" />
    </div>
  )
})
